/**
 * MonitorScreen — route `/rounds/:roundId/monitor` (operator+).
 *
 * Live batch progress via `getRoundStatus` (GET /rounds/{round_id}/status →
 * BatchStatus): state + processed/total + a progress bar + timing. Live rounds
 * show Inicio / Tiempo transcurrido / Última actividad; finished ones show
 * Inicio / Fin / Duración from completed_at — never updated_at, which moves on
 * unrelated edits (see monitor/roundTiming.ts).
 *
 * Polling: a self-scheduling setTimeout (~4s) refetches ONLY while
 * `state ∈ {queued, running}` and STOPS on `completed`/`failed`. No interval is
 * used (so a slow poll never overlaps the next), and the timer is gated on
 * `!IS_DEMO` so the demo state never spins a no-op loop.
 *
 * Branches:
 *   - 404 → the round was never triggered: a dedicated empty state linking to
 *     Operaciones (where an operator would trigger it), NOT a generic alert.
 *   - state=failed → surface BatchStatus.error (the string field) + dashboard CTA.
 *   - state=completed → success view + CTA to the round dashboard.
 *   - 502 / network → ProblemAlert with a retry that restarts the poll. A
 *     background poll error AFTER we already have data keeps the last good
 *     status on screen (a transient blip shouldn't wipe a working monitor).
 *
 * Generated operations used:
 *   - getRoundStatus  GET /rounds/{round_id}/status
 *
 * Auth (401/403) is handled by the route guards (RequireAuth → RequireRole
 * operator). DocumentRef/SignedUpload opacity does not apply to this screen.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useLocation, useParams } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlineOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutlineOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

import { api, isApiError } from "../api/useApi";
import { IS_DEMO } from "../api/client";
import type { ApiError, BatchStatus, Schemas } from "../api/client";
import { Loading } from "../components/Loading";
import { ProblemAlert } from "../components/ProblemAlert";
import { EmptyState } from "../components/EmptyState";
import { DemoBadge } from "../components/DemoBadge";
import { RoundTabs } from "../components/RoundTabs";
import { timingFields } from "./monitor/roundTiming";

type RoundState = Schemas["RoundState"];

/** Poll cadence (ms) while the round is queued/running (spec: 3–5s). */
const POLL_MS = 4000;

/** Live states that keep the poll running. */
const LIVE_STATES: ReadonlySet<RoundState> = new Set<RoundState>([
  "queued",
  "running",
]);

/**
 * Synthetic status for DEMO mode (no API base URL configured). Sourced up front
 * from `IS_DEMO` — NEVER used as an error fallback (mock-on-error is forbidden).
 * Shown as a static "running" snapshot; the timer is gated off in DEMO so this
 * never advances.
 */
const DEMO_STATUS: BatchStatus = {
  round_id: "2026-R2",
  state: "running",
  processed: 742,
  total: 1240,
  pct: 59.8,
  started_at: "2026-06-09T17:42:00Z",
  completed_at: null,
  updated_at: "2026-06-09T18:05:12Z",
  error: null,
};

/** es-MX absolute timestamp, or a placeholder when the server sent null. */
function formatTs(iso: string | null, placeholder: string): string {
  if (!iso) return placeholder;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso; // never crash on a bad date
  return d.toLocaleString("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

const STATE_META: Record<
  RoundState,
  { label: string; color: "default" | "info" | "warning" | "success" | "error" }
> = {
  queued: { label: "En cola", color: "info" },
  running: { label: "Procesando", color: "warning" },
  completed: { label: "Completada", color: "success" },
  failed: { label: "Fallida", color: "error" },
  closed: { label: "Cerrada", color: "default" },
};

function StateChip({ state }: { state: RoundState }) {
  const meta = STATE_META[state] ?? { label: state, color: "default" as const };
  const live = LIVE_STATES.has(state);
  return (
    <Chip
      color={meta.color}
      label={meta.label}
      size="small"
      icon={
        live ? (
          <CircularProgress size={14} color="inherit" thickness={6} />
        ) : undefined
      }
      sx={{ fontWeight: 600 }}
    />
  );
}

function Header({ roundId, polling }: { roundId: string; polling: boolean }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <Typography variant="h1">Monitor de batch</Typography>
        <DemoBadge />
      </Stack>
      <Typography variant="body2" color="text.secondary">
        Progreso en vivo del procesamiento de la ronda.
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Ronda: {roundId}
        {polling && " · Actualizando automáticamente cada ~4 s"}
      </Typography>
    </Box>
  );
}

export default function MonitorScreen() {
  const { roundId } = useParams<{ roundId: string }>();
  const location = useLocation();

  // Status + lifecycle. `firstLoad` distinguishes the initial spinner from a
  // background poll (so we never flash <Loading> on every tick).
  const [status, setStatus] = useState<BatchStatus | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [firstLoad, setFirstLoad] = useState(true);
  const [polling, setPolling] = useState(false);
  // Bumped to restart the poll after a manual retry.
  const [nonce, setNonce] = useState(0);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  // Keep the latest status visible to the (single) effect without re-running it.
  const hadDataRef = useRef(false);
  hadDataRef.current = status !== null;

  useEffect(() => {
    if (!roundId) return;

    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    // DEMO: render the static snapshot once and never start the timer.
    if (IS_DEMO) {
      setStatus(DEMO_STATUS);
      setError(null);
      setFirstLoad(false);
      setPolling(false);
      return;
    }

    async function tick() {
      try {
        const res = await api.GET("/rounds/{round_id}/status", {
          params: { path: { round_id: roundId! } },
        });
        if (!active) return;
        const next = res.data!;
        setStatus(next);
        setError(null);
        setFirstLoad(false);

        // Reschedule ONLY while queued/running; stop on completed/failed.
        if (LIVE_STATES.has(next.state)) {
          setPolling(true);
          timer = setTimeout(tick, POLL_MS);
        } else {
          setPolling(false);
        }
      } catch (err: unknown) {
        if (!active) return;
        const e =
          isApiError(err) || err instanceof Error
            ? err
            : new Error(String(err));

        // Background blip (we already have data): keep the last good status on
        // screen and try again next tick rather than blanking the monitor.
        if (hadDataRef.current && !(isApiError(e) && e.status === 404)) {
          timer = setTimeout(tick, POLL_MS);
          return;
        }

        // First-load failure (incl. 404 "never triggered"): surface it. No
        // reschedule — the user retries via the button (bumps `nonce`).
        setError(e);
        setFirstLoad(false);
        setPolling(false);
      }
    }

    setFirstLoad(true);
    setError(null);
    void tick();

    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [roundId, nonce]);

  if (!roundId) {
    return (
      <Alert severity="error">
        <AlertTitle>Ronda no especificada</AlertTitle>
        Falta el identificador de la ronda en la URL.
      </Alert>
    );
  }

  if (firstLoad && !status && !error) {
    return <Loading label="Cargando el estado de la ronda…" />;
  }

  // 404 → the round was never triggered. Dedicated empty state (operator's next
  // step is Operaciones), not a generic ROUND_NOT_FOUND alert.
  if (isApiError(error) && error.status === 404 && !status) {
    return (
      <Box>
        <RoundTabs roundId={roundId} currentPath={location.pathname} />
        <Header roundId={roundId} polling={false} />
        <EmptyState
          icon={<PlayCircleOutlineIcon fontSize="inherit" />}
          title="La ronda aún no se ha procesado"
          description="No hay un procesamiento en curso ni terminado para esta ronda. Inicia la ingesta y arranca el procesamiento desde Operaciones para ver el avance aquí."
          action={
            <Stack
              direction="row"
              spacing={1.5}
              useFlexGap
              sx={{ flexWrap: "wrap", justifyContent: "center" }}
            >
              <Button
                component={RouterLink}
                to={`/rounds/${roundId}/operations`}
                variant="contained"
                startIcon={<PlayCircleOutlineIcon />}
              >
                Ir a Operaciones
              </Button>
              <Button variant="outlined" onClick={retry}>
                Volver a comprobar
              </Button>
            </Stack>
          }
        />
      </Box>
    );
  }

  // Any other first-load error (502 / network) with no data to fall back on.
  if (error && !status) {
    return (
      <Box>
        <RoundTabs roundId={roundId} currentPath={location.pathname} />
        <Header roundId={roundId} polling={false} />
        <ProblemAlert error={error} onRetry={retry} />
      </Box>
    );
  }

  if (!status) {
    return (
      <Box>
        <RoundTabs roundId={roundId} currentPath={location.pathname} />
        <Header roundId={roundId} polling={false} />
        <EmptyState
          title="Sin datos"
          description="No hay información de estado para esta ronda."
        />
      </Box>
    );
  }

  // A round row is created at birth as `queued` with total=0, BEFORE any
  // processing is launched — that is "not started yet", not "preparing".
  // Without this branch the monitor shows an eternal "Preparando el lote…"
  // for a round whose ingest simply hasn't been processed (2026-07-04 report).
  // Polling stays on so a launch flips this view automatically.
  if (status.state === "queued" && (status.total ?? 0) === 0) {
    return (
      <Box>
        <RoundTabs roundId={roundId} currentPath={location.pathname} />
        <Header roundId={roundId} polling={polling} />
        <EmptyState
          icon={<PlayCircleOutlineIcon fontSize="inherit" />}
          title="La ronda aún no se ha procesado"
          description="Los expedientes cargados quedan en espera hasta que arranques el procesamiento con «Procesar ronda» en Operaciones. Esta pantalla se actualizará sola en cuanto inicie."
          action={
            <Button
              component={RouterLink}
              to={`/rounds/${roundId}/operations`}
              variant="contained"
              startIcon={<PlayCircleOutlineIcon />}
            >
              Ir a Operaciones
            </Button>
          }
        />
      </Box>
    );
  }

  // Bar is driven by processed/total (guaranteed 0–100, also covers total===0
  // while queued). The server `pct` is shown only as a label.
  const total = status.total ?? 0;
  const processed = status.processed ?? 0;
  const barValue =
    total > 0 ? Math.min(100, Math.max(0, (processed / total) * 100)) : 0;
  // processed===0 also animates: the Cloud Run Job pulls a heavy image and
  // loads models before the FIRST expediente completes (1–3 min), and a
  // determinate bar frozen at 0% reads as "stuck" (2026-07-04 report). The
  // worker bumps `processed` per expediente, so once the first one lands the
  // real progress takes over.
  const indeterminate =
    LIVE_STATES.has(status.state) && (total === 0 || processed === 0);
  const coldStarting = indeterminate && total > 0;
  const isFailed = status.state === "failed";
  const isCompleted = status.state === "completed";

  return (
    <Box>
      <RoundTabs roundId={roundId} currentPath={location.pathname} />
      <Header roundId={roundId} polling={polling} />

      {/* If a background poll just failed but we kept the last status, hint it. */}
      {error && status && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          <AlertTitle>Conexión intermitente</AlertTitle>
          No se pudo actualizar el estado en el último intento; se muestra la
          última lectura conocida. Se seguirá intentando.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{
            mb: 2,
            alignItems: { sm: "center" },
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <MonitorHeartIcon color="action" />
            <StateChip state={status.state} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {processed.toLocaleString("es-MX")} /{" "}
            {total.toLocaleString("es-MX")} expedientes
          </Typography>
        </Stack>

        <Box sx={{ mb: 1 }}>
          <LinearProgress
            variant={indeterminate ? "indeterminate" : "determinate"}
            value={barValue}
            color={isFailed ? "error" : isCompleted ? "success" : "primary"}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </Box>
        <Stack direction="row" sx={{ mb: 2, justifyContent: "space-between" }}>
          <Typography variant="caption" color="text.secondary">
            {coldStarting
              ? "Arrancando el procesamiento… encender la infraestructura y los modelos toma 1–3 minutos; el avance aparecerá en cuanto termine el primer expediente."
              : indeterminate
                ? "Preparando el lote…"
                : `${barValue.toLocaleString("es-MX", { maximumFractionDigits: 1 })} % procesado`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            pct (servidor):{" "}
            {status.pct.toLocaleString("es-MX", { maximumFractionDigits: 1 })}
          </Typography>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          }}
        >
          {timingFields(status, Date.now()).map((f) => (
            <Box key={f.label}>
              <Typography variant="caption" color="text.secondary">
                {f.label}
              </Typography>
              <Typography variant="body2">
                {f.iso !== undefined
                  ? formatTs(f.iso, f.placeholder)
                  : (f.text ?? f.placeholder)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Terminal: failed → surface the error string + dashboard CTA. */}
      {isFailed && (
        <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mt: 2 }}>
          <AlertTitle>El procesamiento falló</AlertTitle>
          {status.error
            ? status.error
            : "El procesamiento de la ronda terminó con errores. Revisa el reporte para más detalle."}
          <Box sx={{ mt: 1.5 }}>
            <Stack
              direction="row"
              spacing={1.5}
              useFlexGap
              sx={{ flexWrap: "wrap" }}
            >
              <Button
                component={RouterLink}
                to={`/rounds/${roundId}`}
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<FactCheckIcon />}
              >
                Ver el reporte de la ronda
              </Button>
              <Button color="inherit" size="small" onClick={retry}>
                Reintentar consulta
              </Button>
            </Stack>
          </Box>
        </Alert>
      )}

      {/* Terminal: completed → success + dashboard CTA. */}
      {isCompleted && (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon />}
          sx={{ mt: 2 }}
        >
          <AlertTitle>Procesamiento completado</AlertTitle>
          La ronda terminó de procesarse. El reporte y la cola de revisión ya
          están disponibles.
          <Box sx={{ mt: 1.5 }}>
            <Button
              component={RouterLink}
              to={`/rounds/${roundId}`}
              variant="contained"
              color="success"
              size="small"
              startIcon={<FactCheckIcon />}
            >
              Ir al reporte de la ronda
            </Button>
          </Box>
        </Alert>
      )}

      {/* Live: passive hint while polling. */}
      {(status.state === "queued" || status.state === "running") && (
        <Box sx={{ mt: 2 }}>
          <EmptyState
            icon={<HourglassEmptyIcon fontSize="inherit" />}
            title="Procesamiento en curso"
            description="El avance se actualiza automáticamente. Al terminar, aquí aparecerá el acceso al reporte de la ronda."
          />
        </Box>
      )}
    </Box>
  );
}
